import { useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import de from './de.json';
import en from './en.json';
import fr from './fr.json';
import it from './it.json';
import { I18nContext, type Locale, type I18nContextValue } from './context';
import { getPluralCategory } from './plural';

export type { Locale, I18nContextValue } from './context';

export const LOCALE_LABELS: Record<Locale, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  it: 'Italiano',
};

type Translations = typeof de;

const translations: Record<Locale, Translations> = {
  de,
  en: en as unknown as Translations,
  fr: fr as unknown as Translations,
  it: it as unknown as Translations,
};

// Resolve nested keys like "home.title"
const resolve = (obj: Record<string, any>, path: string): string => {
  const parts = path.split('.');
  let cur: any = obj;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return path;
    cur = cur[part];
  }
  return typeof cur === 'string' ? cur : path;
};

const STORAGE_KEY = 'divecheck-locale';

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && saved in translations) return saved as Locale;
    // Try browser language
    const browserLang = navigator.language.slice(0, 2).toLowerCase();
    if (browserLang in translations) return browserLang as Locale;
    return 'de';
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
    document.documentElement.lang = l;
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      // Plural convention (CLDR-aligned via Intl.PluralRules):
      //   `<key>_zero` | `<key>_one` | `<key>_two` | `<key>_few` |
      //   `<key>_many` | `<key>` (base = `_other`)
      //
      // When `count` or `days` is supplied, we resolve the CLDR plural
      // category for the active locale and try the matching suffixed key
      // first, then fall back to the base key. Languages with >2 plural
      // forms (e.g. ru/pl/cs/ar) get correct grammar without code changes
      // — translators just add the needed `_few`/`_many`/... variants.
      const pluralValue =
        params && typeof params.count === 'number'
          ? params.count
          : params && typeof params.days === 'number'
            ? params.days
            : undefined;

      const tryKeys: string[] = [];
      if (typeof pluralValue === 'number') {
        const category = getPluralCategory(locale, pluralValue);
        if (category && category !== 'other') tryKeys.push(`${key}_${category}`);
        // Back-compat: legacy `_one` variant if no `_one` exists under the
        // CLDR name (covers locales where category resolves to e.g. `one`
        // already — duplicate is harmless because resolve() short-circuits).
        if (pluralValue === 1 && !tryKeys.includes(`${key}_one`)) {
          tryKeys.push(`${key}_one`);
        }
      }
      tryKeys.push(key);

      let str = key;
      for (const k of tryKeys) {
        const candidate = resolve(translations[locale], k);
        if (candidate !== k) { str = candidate; break; }
        const fallback = resolve(translations.de, k);
        if (fallback !== k) { str = fallback; break; }
      }

      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          str = str.replace(`{${k}}`, String(v));
        });
      }
      return str;
    },
    [locale]
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};

// Helper to get translated visibility labels
export const useVisibilityLabels = () => {
  const { t } = useI18n();
  return {
    getLabel: (v: number): string => {
      if (v >= 90) return t('visibility.crystalClear');
      if (v >= 75) return t('visibility.veryGood');
      if (v >= 60) return t('visibility.good');
      if (v >= 45) return t('visibility.ok');
      if (v >= 30) return t('visibility.slightlyMurky');
      if (v >= 15) return t('visibility.murky');
      return t('visibility.veryMurky');
    },
    getLevelLabel: (level: string): string => {
      switch (level) {
        case 'excellent': return t('visibility.excellent');
        case 'good': return t('visibility.goodRange');
        case 'moderate': return t('visibility.moderateRange');
        case 'poor': return t('visibility.poorRange');
        case 'very-poor': return t('visibility.veryPoor');
        default: return level;
      }
    },
  };
};

// Translated viz info for the history timeline (1-5 scale)
export const useVizInfo = () => {
  const { t } = useI18n();
  return (avg: number): { label: string; hsl: string; textClass: string } => {
    if (avg >= 6.5) return { label: t('visibility.crystalClear'), hsl: 'hsl(200, 85%, 82%)', textClass: 'text-[hsl(200,85%,72%)]' };
    if (avg >= 5.5) return { label: t('visibility.veryGood'),    hsl: 'hsl(195, 70%, 75%)', textClass: 'text-[hsl(195,70%,65%)]' };
    if (avg >= 4.5) return { label: t('visibility.good'),        hsl: 'hsl(190, 55%, 65%)', textClass: 'text-[hsl(190,55%,58%)]' };
    if (avg >= 3.5) return { label: t('visibility.ok'),          hsl: 'hsl(170, 40%, 50%)', textClass: 'text-primary' };
    if (avg >= 2.5) return { label: t('visibility.slightlyMurky'), hsl: 'hsl(80, 18%, 40%)', textClass: 'text-rating' };
    if (avg >= 1.5) return { label: t('visibility.murky'),       hsl: 'hsl(40, 30%, 32%)', textClass: 'text-destructive' };
    return              { label: t('visibility.veryMurky'),    hsl: 'hsl(25, 35%, 24%)', textClass: 'text-destructive' };
  };
};

// Translated monthly label
export const useMonthlyLabel = () => {
  const { t } = useI18n();
  return (v: number): string => {
    if (v >= 75) return t('visibility.veryGood');
    if (v >= 60) return t('visibility.good');
    if (v >= 45) return t('visibility.ok');
    if (v >= 30) return t('visibility.murky');
    return t('visibility.poor');
  };
};
